import styled, { css, keyframes } from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";

export const Container = styled(Box)<{ $fullscreen?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  height: ${({ $fullscreen }) =>
    $fullscreen ? "100vh" : "calc(100vh - 73px)"};
  max-height: ${({ $fullscreen }) =>
    $fullscreen ? "100vh" : "calc(100vh - 73px)"};
  background: #0b1116;
  color: #f7fcfd;
  overflow: hidden;

  ${({ $fullscreen }) =>
    $fullscreen &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
  `}
`;

export const HeaderWrapper = styled(Box)<{ $bgImage?: string }>`
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0px;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${({ $bgImage }) =>
      $bgImage ? `url(${$bgImage})` : "none"};
    background-size: cover;
    background-position: center 20%;
    filter: blur(5px);
    opacity: 0.3;
    transform: scale(1);
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(19, 29, 37, 0.75) 0%,
      rgba(19, 29, 37, 0.85) 100%
    );
    z-index: 1;
  }
`;

export const Header = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  position: relative;
  z-index: 2;
  border-bottom: 1px solid rgba(30, 46, 58, 0.5);
`;

export const MapBadge = styled(Box)`
  padding: 6px 12px;
  background: rgba(113, 170, 255, 0.1);
  border: 1px solid rgba(113, 170, 255, 0.3);
  border-radius: 6px;
`;

export const MainContent = styled(Flex)`
  flex: 1;
  overflow: hidden;
  min-height: 0;
`;

export const TimelinePanel = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0b1116;
  position: relative;
  min-height: 0;
  overflow: hidden;
`;

export const ControlBar = styled(Flex)<{ $inWrapper?: boolean }>`
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${({ $inWrapper }) => ($inWrapper ? "transparent" : "#131d25")};
  border-bottom: 1px solid #1e2e3a;
  position: relative;
  z-index: 2;

  @media (max-width: 1600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const MinimapContainer = styled(Box)`
  position: relative;
  padding: 8px 16px;
  background: #0d1519;
  border-bottom: 1px solid #1e2e3a;
`;

export const MinimapLabel = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  color: #557077;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

export const RoundIndicator = styled(Flex)`
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: #0b1116;
  border: 1px solid #1e2e3a;
  border-radius: 6px;
  position: relative;
`;

export const RoundPip = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 2px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.1s ease;
  background: ${({ active }) =>
    active ? "rgba(113, 170, 255, 0.4)" : "rgba(143, 169, 175, 0.1)"};

  .round-num {
    font-size: 9px;
    font-weight: 600;
    color: ${({ active }) => (active ? "#71aaff" : "#557077")};
    line-height: 1;
  }

  .kills-count {
    display: none;
  }

  &:hover {
    background: rgba(113, 170, 255, 0.3);
  }
`;

export const HalftimeDivider = styled.div`
  width: 1px;
  height: 16px;
  background: #253a49;
  margin: 0 4px;
`;

export const ViewportIndicator = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  border: 2px solid #71aaff;
  border-radius: 4px;
  pointer-events: none;
  box-shadow: 0 0 8px rgba(113, 170, 255, 0.3);
  z-index: 1;
`;

export const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #253a49;
    color: #f7fcfd;
  }
`;

export const WeaponSelect = styled.select`
  padding: 6px 28px 6px 10px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #f7fcfd;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238fa9af'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;

  &:hover {
    background-color: #253a49;
    border-color: #71aaff;
  }

  &:focus {
    outline: none;
    border-color: #71aaff;
  }

  option {
    background: #1a2933;
    color: #f7fcfd;
  }
`;

export const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

export const ToggleCheckbox = styled.input`
  display: none;
`;

export const ToggleSlider = styled.span`
  position: relative;
  width: 36px;
  height: 20px;
  background: #253a49;
  border-radius: 10px;
  transition: all 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #8fa9af;
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  ${ToggleCheckbox}:checked + & {
    background: #4dbd94;
  }

  ${ToggleCheckbox}:checked + &::after {
    left: 18px;
    background: #0b1116;
  }
`;

export const TimelineContainerWrapper = styled(Box)`
  flex: 1;
  position: relative;
  min-height: 0;
  overflow: hidden;
`;

export const TimelineContainer = styled(Box)`
  width: 100%;
  height: calc(100% + 20px); /* Extra height to push scrollbar out of view */
  overflow-x: scroll;
  overflow-y: auto;
  position: relative;
  background: #0b1116;
  padding-bottom: 20px; /* Compensate for the extra height */

  /* Vertical scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 20px;
  }

  &::-webkit-scrollbar-track:vertical {
    background: #0b1116;
  }

  &::-webkit-scrollbar-thumb:vertical {
    background: #253a49;
    border-radius: 4px;

    &:hover {
      background: #3d5a6e;
    }
  }

  &::-webkit-scrollbar-corner {
    background: #0b1116;
  }
`;

export const CustomScrollbarContainer = styled(Box)`
  position: relative;
  height: 24px;
  background: #131d25;
  border-top: 1px solid #1e2e3a;
  padding: 4px 16px 4px 216px;
  flex-shrink: 0;
`;

export const CustomScrollbarTrack = styled(Box)`
  position: relative;
  height: 16px;
  background: #1a2530;
  border-radius: 8px;
  border: 1px solid #253a49;
  cursor: pointer;
`;

export const CustomScrollbarThumb = styled(Box)<{ $isDragging?: boolean }>`
  position: absolute;
  top: 2px;
  height: 10px;
  min-width: 40px;
  background: ${({ $isDragging }) =>
    $isDragging
      ? "linear-gradient(180deg, #71aaff 0%, #5090e0 100%)"
      : "linear-gradient(180deg, #4d6a7e 0%, #3d5665 100%)"};
  border-radius: 5px;
  cursor: grab;
  transition: ${({ $isDragging }) =>
    $isDragging ? "none" : "background 0.15s ease"};

  &:hover {
    background: linear-gradient(180deg, #5d7a8e 0%, #4d6675 100%);
  }

  &:active {
    cursor: grabbing;
    background: linear-gradient(180deg, #71aaff 0%, #5090e0 100%);
  }
`;

export const TimelineInner = styled(Box)`
  position: relative;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

export const TimelineRuler = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #131d25;
`;

export const RulerHeader = styled(Flex)`
  height: 24px;
`;

export const RoundsHeader = styled(Flex)`
  height: 28px;
  background: #0f171d;
`;

export const PlayerColumnHeader = styled(Flex)`
  position: sticky;
  left: 0;
  z-index: 15;
  align-items: center;
  width: 200px;
  min-width: 200px;
  padding: 0 12px;
  background: #131d25;
  font-size: 11px;
  font-weight: 600;
  color: #557077;
`;

export const RulerRow = styled(Box)`
  position: relative;
  flex: 1;
  height: 100%;
  border-bottom: 1px solid #1e2e3a;
`;

export const RulerMark = styled(Box)`
  position: absolute;
  top: 0;
  height: 100%;
  border-left: 1px solid #253a49;

  span {
    position: absolute;
    top: 4px;
    left: 4px;
    font-size: 10px;
    color: #557077;
    white-space: nowrap;
  }
`;

export const RoundsRow = styled(Box)`
  position: relative;
  flex: 1;
  height: 100%;
`;

export const RoundBlock = styled(Box)`
  position: absolute;
  height: 100%;
  background: rgba(143, 169, 175, 0.06);
  border-left: 1px solid rgba(143, 169, 175, 0.2);
  border-bottom: 2px solid rgba(143, 169, 175, 0.15);
`;

export const RoundLabel = styled(Flex)`
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(247, 252, 253, 0.7);
`;

export const RoundScore = styled.span`
  font-size: 10px;
  font-weight: 600;

  .ct {
    color: #4da9c9;
  }

  .separator {
    color: rgba(143, 169, 175, 0.5);
    margin: 0 1px;
  }

  .t {
    color: #dea34e;
  }
`;

export const PlayersContainer = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const TeamDivider = styled(Flex)`
  align-items: center;
  height: 24px;
  margin: 8px 0;
  padding: 0 12px;
`;

export const TeamDividerLine = styled.div`
  flex: 1;
  height: 2px;
  background: linear-gradient(to right, #8fa9af, rgba(143, 169, 175, 0.1));
  border-radius: 1px;
`;

const playerFocusPulse = keyframes`
  0%, 100% {
    box-shadow: inset 0 0 0 2px rgba(187, 134, 252, 0.4);
  }
  50% {
    box-shadow: inset 0 0 0 2px rgba(187, 134, 252, 0.8);
  }
`;

export const PlayerRow = styled(Flex)<{
  selected: boolean;
  team?: "ct" | "t";
  $highlightFocused?: boolean;
}>`
  position: relative;
  height: 56px;
  margin: 4px 0;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 4px;

  &:hover {
    background: ${({ team }) =>
      team === "ct" ? "rgba(92, 155, 255, 0.08)" : "rgba(232, 169, 62, 0.08)"};
  }

  ${({ selected, team }) =>
    selected &&
    css`
      background: ${team === "ct"
        ? "rgba(92, 155, 255, 0.2)"
        : "rgba(232, 169, 62, 0.2)"};
      box-shadow: inset 0 0 0 2px
        ${team === "ct" ? "rgba(92, 155, 255, 0.4)" : "rgba(232, 169, 62, 0.4)"};
    `}

  ${({ $highlightFocused }) =>
    $highlightFocused &&
    css`
      background: rgba(187, 134, 252, 0.1);
      animation: ${playerFocusPulse} 2s ease-in-out infinite;
    `}
`;

export const PlayerInfo = styled(Flex)<{
  selected: boolean;
  isCurrentUser?: boolean;
  team?: "ct" | "t";
}>`
  position: sticky;
  left: 0;
  z-index: 100;
  align-items: center;
  gap: 10px;
  width: 200px;
  min-width: 200px;
  padding: 8px 12px;
  background: ${({ selected, isCurrentUser, team }) =>
    isCurrentUser
      ? selected
        ? "#1a3d2e"
        : "#152a20"
      : selected
      ? team === "ct"
        ? "#1a3348"
        : "#2d2a1a"
      : team === "ct"
      ? "#131d25"
      : "#1a1815"};
  border-right: 1px solid #1e2e3a;
  border-left: 4px solid
    ${({ selected, isCurrentUser, team }) =>
      isCurrentUser
        ? "#4dbd94"
        : selected
        ? team === "ct"
          ? "#5C9BFF"
          : "#E8A93E"
        : team === "ct"
        ? "rgba(92, 155, 255, 0.3)"
        : "rgba(232, 169, 62, 0.3)"};
`;

export const PlayerAvatar = styled(Flex)<{
  selected: boolean;
  isCurrentUser?: boolean;
  team?: "ct" | "t";
}>`
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ selected, isCurrentUser, team }) =>
    isCurrentUser
      ? "#4dbd94"
      : selected
      ? team === "ct"
        ? "#5C9BFF"
        : "#E8A93E"
      : "#253a49"};
  color: ${({ selected, isCurrentUser }) =>
    isCurrentUser || selected ? "#0b1116" : "#8fa9af"};
  box-shadow: ${({ selected, isCurrentUser, team }) =>
    selected && !isCurrentUser
      ? team === "ct"
        ? "0 0 0 2px rgba(92, 155, 255, 0.5)"
        : "0 0 0 2px rgba(232, 169, 62, 0.5)"
      : isCurrentUser
      ? "0 0 0 2px rgba(77, 189, 148, 0.5)"
      : "none"};
`;

export const PlayerAvatarImage = styled.img<{
  selected: boolean;
  isCurrentUser?: boolean;
  team?: "ct" | "t";
}>`
  border-radius: 50%;
  box-shadow: ${({ selected, isCurrentUser, team }) =>
    selected && !isCurrentUser
      ? team === "ct"
        ? "0 0 0 2px rgba(92, 155, 255, 0.5)"
        : "0 0 0 2px rgba(232, 169, 62, 0.5)"
      : isCurrentUser
      ? "0 0 0 2px rgba(77, 189, 148, 0.5)"
      : "none"};
  height: 32px;
  object-fit: cover;
  width: 32px;
`;

export const PlayerDetails = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const PlayerRemoveHint = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: rgba(249, 83, 112, 0.2);
  border: 1px solid rgba(249, 83, 112, 0.4);
  border-radius: 4px;
  color: #f95370;
  font-size: 10px;
  font-weight: 600;
  opacity: 0;
  cursor: pointer;
  transition: all 0.15s ease;

  ${PlayerRow}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(249, 83, 112, 0.4);
    border-color: rgba(249, 83, 112, 0.6);
  }
`;

export const PlayerName = styled(Text)<{
  selected: boolean;
  isCurrentUser?: boolean;
}>`
  font-size: 13px;
  font-weight: ${({ selected, isCurrentUser }) =>
    selected || isCurrentUser ? 600 : 500};
  color: ${({ selected, isCurrentUser }) =>
    isCurrentUser ? "#5cd4a6" : selected ? "#5C9BFF" : "#8fa9af"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const YouBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #0b1116;
  background: #4dbd94;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
`;

export const PlayerStats = styled(Flex)`
  gap: 8px;
  margin-top: 2px;
`;

export const StatBadge = styled(Flex)<{ type: "kills" | "deaths" }>`
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: ${({ type }) => (type === "kills" ? "#4dbd94" : "#f95370")};
`;

export const PlayerTimeline = styled(Box)<{ selected: boolean }>`
  position: relative;
  flex: 1;
  height: 100%;
  cursor: ${({ selected }) => (selected ? "crosshair" : "pointer")};
`;

export const RoundSegment = styled(Box)<{
  team: "ct" | "t" | null;
  selected: boolean;
}>`
  position: absolute;
  top: 8px;
  height: calc(100% - 16px);
  background: ${({ team, selected }) =>
    team === "ct"
      ? selected
        ? "rgba(92, 155, 255, 0.15)"
        : "rgba(92, 155, 255, 0.08)"
      : team === "t"
      ? selected
        ? "rgba(232, 169, 62, 0.15)"
        : "rgba(232, 169, 62, 0.08)"
      : "rgba(143, 169, 175, 0.04)"};
  border-left: 1px solid rgba(143, 169, 175, 0.15);
  border-bottom: 2px solid
    ${({ team, selected }) =>
      team === "ct"
        ? selected
          ? "rgba(92, 155, 255, 0.5)"
          : "rgba(92, 155, 255, 0.25)"
        : team === "t"
        ? selected
          ? "rgba(232, 169, 62, 0.5)"
          : "rgba(232, 169, 62, 0.25)"
        : "rgba(143, 169, 175, 0.1)"};
`;

export const KillMarker = styled(Flex)<{
  isHeadshot: boolean;
  selected: boolean;
  inSegment?: boolean;
}>`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  align-items: center;
  gap: 4px;
  padding: ${({ selected }) => (selected ? "4px 8px" : "4px 6px")};
  background: ${({ isHeadshot }) => (isHeadshot ? "#e8a93e" : "#4dbd94")};
  border-radius: ${({ selected }) => (selected ? "12px" : "10px")};
  color: #0b1116;
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s ease;
  min-width: 24px;
  min-height: 20px;
  justify-content: center;
  box-shadow: ${({ inSegment }) =>
    inSegment ? "0 0 0 2px #0b1116, 0 0 0 4px #f7fcfd" : "none"};

  &:hover {
    transform: translate(-50%, -50%) scale(1.15);
    z-index: 10;
  }
`;

export const WeaponIcon = styled.img`
  height: 14px;
  width: auto;
  max-width: 28px;
  filter: brightness(0) saturate(100%);
  object-fit: contain;
`;

export const KillLabel = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  color: #0b1116;
  white-space: nowrap;
`;

export const DeathMarker = styled(Flex)<{
  selected: boolean;
  inSegment?: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(249, 83, 112, 0.3);
  border: 1px solid #f95370;
  border-radius: 50%;
  color: #f95370;
  opacity: ${({ selected }) => (selected ? 1 : 0.5)};
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s ease;
  box-shadow: ${({ inSegment }) =>
    inSegment ? "0 0 0 2px #0b1116, 0 0 0 4px #f7fcfd" : "none"};

  &:hover {
    transform: translate(-50%, -50%) scale(1.15);
    z-index: 10;
    opacity: 1;
    background: rgba(249, 83, 112, 0.5);
  }
`;

export const SelectionOverlay = styled(Box)<{
  isActive?: boolean;
  wouldMerge?: boolean;
  selected?: boolean;
  hasJumpCuts?: boolean;
}>`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background: ${({ isActive, wouldMerge, selected, hasJumpCuts }) =>
    hasJumpCuts
      ? "rgba(77, 189, 148, 0.08)"
      : wouldMerge
      ? "rgba(113, 170, 255, 0.4)"
      : selected
      ? "rgba(77, 189, 148, 0.4)"
      : isActive
      ? "rgba(77, 189, 148, 0.3)"
      : "rgba(77, 189, 148, 0.2)"};
  border: 2px ${({ hasJumpCuts }) => (hasJumpCuts ? "dashed" : "solid")}
    ${({ isActive, wouldMerge, selected, hasJumpCuts }) =>
      hasJumpCuts
        ? "rgba(77, 189, 148, 0.4)"
        : wouldMerge
        ? "#71AAFF"
        : selected
        ? "#f7fcfd"
        : isActive
        ? "#5cd4a6"
        : "#4dbd94"};
  border-radius: 6px;
  z-index: 4;
  transition: all 0.15s ease;
  cursor: pointer;
`;

export const JumpCutSegmentOverlay = styled(Box)<{
  selected?: boolean;
}>`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background: ${({ selected }) =>
    selected ? "rgba(77, 189, 148, 0.5)" : "rgba(77, 189, 148, 0.35)"};
  border: 2px solid ${({ selected }) => (selected ? "#f7fcfd" : "#4dbd94")};
  border-radius: 4px;
  z-index: 5;
  pointer-events: none;
`;

export const PreviewOverlay = styled(Box)<{ wouldMerge?: boolean }>`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background: ${({ wouldMerge }) =>
    wouldMerge ? "rgba(113, 170, 255, 0.2)" : "rgba(77, 189, 148, 0.15)"};
  border: 2px dashed ${({ wouldMerge }) => (wouldMerge ? "#71AAFF" : "#4dbd94")};
  border-radius: 6px;
  z-index: 3;
  pointer-events: none;
`;

export const SelectionHandle = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 28px;
  background: #4dbd94;
  border-radius: 3px;
  cursor: ew-resize;
  z-index: 10;
  transition: background 0.15s ease;

  &:hover {
    background: #5dd4a8;
  }

  &.left {
    left: -4px;
  }
  &.right {
    right: -4px;
  }
`;

export const SelectionsPanel = styled(Box)<{ collapsed?: boolean }>`
  width: ${({ collapsed }) => (collapsed ? "48px" : "400px")};
  background: #131d25;
  border-left: 1px solid #1e2e3a;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease;
  overflow: hidden;
`;

export const PanelHeader = styled(Box)`
  padding: 16px;
  border-bottom: 1px solid #1e2e3a;
`;

export const PanelHeaderTop = styled(Flex)`
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

export const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(143, 169, 175, 0.1);
  border: none;
  border-radius: 4px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(143, 169, 175, 0.2);
    color: #f7fcfd;
  }
`;

export const Toast = styled(Box)`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #1a2933 0%, #253a49 100%);
  border: 1px solid rgba(113, 170, 255, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  color: #f7fcfd;
  font-size: 13px;
  font-weight: 500;
  z-index: 1000;
  animation: slideUp 0.3s ease;

  svg {
    color: #71aaff;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

export const SelectionsList = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #0b1116;
  }
  &::-webkit-scrollbar-thumb {
    background: #253a49;
    border-radius: 3px;
  }
`;

export const EmptyState = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 16px;
`;

export const EmptyIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #1a2933;
  border-radius: 50%;
  color: #557077;
`;

const highlightPulse = keyframes`
  0% {
    border-color: #71aaff;
    box-shadow: 0 0 0 0 rgba(113, 170, 255, 0.4);
  }
  50% {
    border-color: #71aaff;
    box-shadow: 0 0 8px 2px rgba(113, 170, 255, 0.3);
  }
  100% {
    border-color: #1e2e3a;
    box-shadow: 0 0 0 0 rgba(113, 170, 255, 0);
  }
`;

const borderGlow = keyframes`
  0% {
    border-color: rgba(77, 189, 148, 0.25);
  }
  50% {
    border-color: rgba(77, 189, 148, 0.4);
  }
  100% {
    border-color: rgba(77, 189, 148, 0.25);
  }
`;

export const SelectionCard = styled(Box)<{
  selected?: boolean;
  recentlyAdded?: boolean;
}>`
  padding: 10px 12px;
  margin-bottom: 8px;
  background: ${({ selected }) => (selected ? "#1a2a36" : "#16232c")};
  border: 1px solid ${({ selected }) => (selected ? "#4dbd94" : "#1e2e3a")};
  border-radius: 8px;
  transition: all 0.15s ease;
  overflow: hidden;
  cursor: pointer;

  ${({ recentlyAdded }) =>
    recentlyAdded &&
    css`
      animation: ${highlightPulse} 2s ease-out forwards;
    `}

  &:hover {
    border-color: ${({ selected }) => (selected ? "#4dbd94" : "#253a49")};
  }
`;

export const SegmentDoneButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(77, 189, 148, 0.15);
  border: 1px solid rgba(77, 189, 148, 0.3);
  border-radius: 4px;
  color: #4dbd94;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-left: auto;

  &:hover {
    background: rgba(77, 189, 148, 0.25);
    border-color: rgba(77, 189, 148, 0.5);
  }
`;

export const SegmentExpandHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 0;
  color: #8fa9af;
  font-size: 12px;
  background: rgba(77, 189, 148, 0.05);
  border-radius: 6px;
  margin-top: 4px;
  transition: all 0.15s ease;

  svg {
    color: #4dbd94;
  }

  &:hover {
    background: rgba(77, 189, 148, 0.1);
    color: #f7fcfd;
  }
`;

export const LocateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(113, 170, 255, 0.15);
  border: 1px solid rgba(113, 170, 255, 0.3);
  border-radius: 4px;
  color: #71aaff;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(113, 170, 255, 0.25);
    border-color: rgba(113, 170, 255, 0.5);
  }
`;

// Segment Card Tabs
export const SegmentTabs = styled(Flex)`
  border-bottom: 1px solid #1e2e3a;
  margin: 0 -12px;
  padding: 0 12px;
`;

export const SegmentTab = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 10px 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? "#4dbd94" : "transparent")};
  color: ${({ $active }) => ($active ? "#f7fcfd" : "#557077")};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ $active }) => ($active ? "#f7fcfd" : "#8fa9af")};
  }
`;

export const SegmentTabContent = styled(Box)`
  padding-top: 12px;
`;

export const TabInstruction = styled(Text)`
  font-size: 11px;
  color: #8fa9af;
  margin-bottom: 12px;
  line-height: 1.5;
`;

export const ClipHeader = styled(Flex)`
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

export const DurationSlider = styled(Box)`
  margin: 8px 0;
  padding: 0 4px;
`;

export const DurationTrack = styled(Box)`
  position: relative;
  height: 10px;
  background: rgba(37, 58, 73, 0.8);
  border: 1px solid rgba(37, 58, 73, 0.5);
  border-radius: 5px;
  margin-bottom: 6px;
`;

export const DurationFill = styled(Box)<{ hasJumpCuts?: boolean }>`
  position: absolute;
  top: 0;
  height: 100%;
  background: ${({ hasJumpCuts }) =>
    hasJumpCuts
      ? "rgba(77, 189, 148, 0.15)"
      : "linear-gradient(to right, rgba(77, 189, 148, 0.4), rgba(77, 189, 148, 0.6))"};
  border-radius: 4px;
`;

export const DurationJumpCutSegment = styled(Box)`
  position: absolute;
  top: 0;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(77, 189, 148, 0.5),
    rgba(77, 189, 148, 0.7)
  );
  border-radius: 3px;
  z-index: 1;
`;

export const DurationKillMarker = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: #4dbd94;
  border: 2px solid #0b1116;
  border-radius: 50%;
  z-index: 2;
  box-shadow: 0 0 4px rgba(77, 189, 148, 0.5);
`;

export const DurationDeathMarker = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: #e74c3c;
  border-radius: 50%;
  z-index: 2;
`;

export const DurationLabels = styled(Flex)`
  justify-content: space-between;
  font-size: 12px;
  font-weight: 500;
  color: #8fa9af;
  font-family: monospace;
`;

export const DurationTimingLabels = styled(Flex)`
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #8fa3ad;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(77, 189, 148, 0.05);
  border-radius: 6px;

  span {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .label {
    color: #8fa9af;
  }

  .value {
    color: #4dbd94;
    font-weight: 600;
    font-family: monospace;
  }
`;

export const KillsList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const KillItem = styled(Box)`
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;

export const KillItemHeader = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 12px;

  .killer {
    color: #f7fcfd;
    font-weight: 500;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .victim {
    color: #8fa9af;
    font-weight: 500;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hs {
    font-size: 9px;
    padding: 1px 4px;
    background: rgba(249, 83, 112, 0.3);
    color: #f95370;
    border-radius: 3px;
    font-weight: 600;
  }
`;

export const KillWeaponIcon = styled.img`
  width: 24px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(73%) sepia(9%) saturate(590%)
    hue-rotate(144deg) brightness(90%) contrast(86%);
`;

export const KillItemInfo = styled(Box)`
  flex: 1;

  .weapon {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #f7fcfd;

    .hs {
      font-size: 10px;
      padding: 2px 5px;
      background: rgba(249, 83, 112, 0.3);
      color: #f95370;
      border-radius: 3px;
    }
  }

  .players {
    font-size: 12px;
    color: #557077;
  }
`;

export const PovSection = styled(Box)`
  margin-bottom: 10px;
`;

export const PovLabel = styled(Text)`
  font-size: 11px;
  color: #557077;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const PovToggle = styled(Flex)`
  gap: 8px;
`;

const getTeamColors = (
  team: "ct" | "t" | null | undefined,
  active: boolean,
) => {
  if (team === "ct") {
    return {
      bg: active ? "rgba(92, 155, 255, 0.35)" : "rgba(92, 155, 255, 0.08)",
      border: active ? "#5c9bff" : "rgba(92, 155, 255, 0.3)",
      color: active ? "#fff" : "#7eb3ff",
      shadow: active ? "0 0 8px rgba(92, 155, 255, 0.4)" : "none",
      hoverBg: "rgba(92, 155, 255, 0.15)",
    };
  } else if (team === "t") {
    return {
      bg: active ? "rgba(232, 169, 62, 0.35)" : "rgba(232, 169, 62, 0.08)",
      border: active ? "#e8a93e" : "rgba(232, 169, 62, 0.3)",
      color: active ? "#fff" : "#f0c06a",
      shadow: active ? "0 0 8px rgba(232, 169, 62, 0.4)" : "none",
      hoverBg: "rgba(232, 169, 62, 0.15)",
    };
  }
  return {
    bg: active ? "rgba(113, 170, 255, 0.3)" : "transparent",
    border: active ? "#71aaff" : "#253a49",
    color: active ? "#fff" : "#557077",
    shadow: active ? "0 0 8px rgba(113, 170, 255, 0.4)" : "none",
    hoverBg: "rgba(113, 170, 255, 0.1)",
  };
};

export const PovOption = styled.button<{
  active: boolean;
  team?: "ct" | "t" | null;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: ${({ active, team }) => getTeamColors(team, active).bg};
  border: 2px solid ${({ active, team }) => getTeamColors(team, active).border};
  border-radius: 6px;
  font-size: 11px;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  color: ${({ active, team }) => getTeamColors(team, active).color};
  box-shadow: ${({ active, team }) => getTeamColors(team, active).shadow};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  flex: 1;
  justify-content: center;
  position: relative;

  .player-name {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 70px;
  }

  .check-icon {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 14px;
    height: 14px;
    background: ${({ team }) =>
      team === "ct" ? "#5c9bff" : team === "t" ? "#e8a93e" : "#71aaff"};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0b1116;
    font-size: 8px;
  }

  &:hover {
    background: ${({ team }) => getTeamColors(team, true).hoverBg};
    border-color: ${({ team }) => getTeamColors(team, true).border};
  }
`;

export const PovAvatar = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export const PovAvatarPlaceholder = styled(Flex)<{ team?: "ct" | "t" | null }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ team }) =>
    team === "ct"
      ? "rgba(92, 155, 255, 0.3)"
      : team === "t"
      ? "rgba(232, 169, 62, 0.3)"
      : "rgba(143, 169, 175, 0.3)"};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ team }) =>
    team === "ct" ? "#5c9bff" : team === "t" ? "#e8a93e" : "#8fa9af"};
`;

export const MountedCameraSection = styled(Box)`
  margin-top: 8px;
`;

export const MountedCameraHeader = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #8fa9af;
  margin-bottom: 8px;

  svg {
    color: #71aaff;
  }
`;

export const MountedCameraOptions = styled(Flex)`
  flex-wrap: wrap;
  gap: 6px;
`;

export const MountedCameraOption = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: ${({ active }) =>
    active ? "rgba(113, 170, 255, 0.2)" : "rgba(30, 46, 58, 0.5)"};
  border: 1px solid ${({ active }) => (active ? "#71aaff" : "#253a49")};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  .label {
    font-size: 11px;
    font-weight: ${({ active }) => (active ? 600 : 400)};
    color: ${({ active }) => (active ? "#71aaff" : "#8fa9af")};
  }

  &:hover {
    background: rgba(113, 170, 255, 0.15);
    border-color: #71aaff;

    .label {
      color: #71aaff;
    }
  }
`;

export const SectionDivider = styled(Box)`
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(37, 58, 73, 0.8),
    transparent
  );
  margin: 12px 0;
`;

export const CameraOptionsSection = styled(Box)`
  padding: 10px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  margin-top: 4px;
`;

export const CameraOptionsHeader = styled(Flex)`
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8fa9af;
  margin-bottom: 10px;

  svg {
    color: #71aaff;
  }
`;

export const CameraKillItem = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(11, 17, 22, 0.5);
  border: 1px solid #1e2e3a;
  border-radius: 6px;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

export const CameraKillInfo = styled(Flex)`
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const CameraKillNumber = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  color: #71aaff;
  background: rgba(113, 170, 255, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
`;

export const CameraKillPlayers = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 12px;
  min-width: 0;

  .killer {
    color: #f7fcfd;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 80px;
  }

  .arrow {
    color: #557077;
    flex-shrink: 0;
  }

  .victim {
    color: #8fa9af;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 80px;
  }
`;

export const CameraPovSwitch = styled(Flex)`
  gap: 4px;
  flex-shrink: 0;
`;

export const CameraPovBtn = styled.button<{
  $active: boolean;
  $team?: "ct" | "t" | null;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: ${({ $active, $team }) =>
    $active
      ? $team === "ct"
        ? "rgba(92, 155, 255, 0.25)"
        : $team === "t"
        ? "rgba(232, 169, 62, 0.25)"
        : "rgba(77, 189, 148, 0.25)"
      : "transparent"};
  border: 1px solid
    ${({ $active, $team }) =>
      $active
        ? $team === "ct"
          ? "#5c9bff"
          : $team === "t"
          ? "#e8a93e"
          : "#4dbd94"
        : "#253a49"};
  border-radius: 4px;
  font-size: 11px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, $team }) =>
    $active
      ? $team === "ct"
        ? "#7eb3ff"
        : $team === "t"
        ? "#f0c06a"
        : "#4dbd94"
      : "#557077"};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ $team }) =>
      $team === "ct" ? "#5c9bff" : $team === "t" ? "#e8a93e" : "#4dbd94"};
  }
`;

export const CameraKillHeader = styled(Flex)`
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 10px;

  .killer {
    color: #f7fcfd;
    font-weight: 500;
  }

  .victim {
    color: #8fa9af;
    font-weight: 500;
  }
`;

export const TimeAdjustRow = styled(Flex)`
  gap: 12px;
  width: 100%;
  padding: 12px;
  background: rgba(11, 17, 22, 0.5);
  border: 1px solid #1e2e3a;
  border-radius: 8px;
  margin-top: 8px;
`;

export const TimeAdjustGroup = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const TimeLabel = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 8px;
  display: block;
  text-align: center;
`;

export const TimeInputWrapper = styled(Flex)`
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
`;

export const TimeIncrementBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 4px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #253a49;
    border-color: #4dbd94;
    color: #4dbd94;
  }

  &:active:not(:disabled) {
    background: rgba(77, 189, 148, 0.2);
    color: #4dbd94;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const TimeInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: #0d1519;
  border: 2px solid #253a49;
  border-radius: 6px;
  color: #f7fcfd;
  font-size: 14px;
  font-family: monospace;
  font-weight: 600;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4dbd94;
    box-shadow: 0 0 0 2px rgba(77, 189, 148, 0.2);
  }
`;

export const JumpCutsSection = styled(Box)`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #1e2e3a;
`;

export const JumpCutsToggle = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: rgba(11, 17, 22, 0.5);
  border: 1px solid #1e2e3a;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  &:hover {
    border-color: #4dbd94;
    background: rgba(77, 189, 148, 0.05);
  }

  &:active {
    background: rgba(77, 189, 148, 0.1);
  }

  /* Make inner elements not capture clicks */
  & > * {
    pointer-events: none;
  }
`;

export const JumpCutsLabel = styled(Text)`
  font-size: 13px;
  font-weight: 500;
  color: #f7fcfd;
`;

export const JumpCutsDescription = styled(Text)`
  font-size: 11px;
  color: #8fa9af;
  margin-top: 2px;
`;

export const JumpCutsKillList = styled(Box)`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const JumpCutsKillItem = styled(Flex)`
  align-items: center;
  padding: 10px 12px;
  background: rgba(11, 17, 22, 0.5);
  border: 1px solid #1e2e3a;
  border-radius: 6px;
  gap: 10px;
`;

export const JumpCutsKillNumber = styled(Text)`
  font-size: 11px;
  font-weight: 600;
  color: #71aaff;
  background: rgba(113, 170, 255, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
  min-width: 24px;
  text-align: center;
`;

export const JumpCutsKillInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const JumpCutsKillPlayer = styled(Text)`
  font-size: 12px;
  color: #f7fcfd;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const JumpCutsKillWeapon = styled(Text)`
  font-size: 10px;
  color: #8fa9af;
`;

export const JumpCutsTimingControls = styled(Flex)`
  gap: 6px;
  align-items: center;
`;

export const JumpCutsTimingGroup = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

export const JumpCutsTimingLabel = styled(Text)`
  font-size: 9px;
  color: #8fa9af;
  text-transform: uppercase;
`;

export const JumpCutsTimingValue = styled(Flex)`
  align-items: center;
  gap: 2px;
`;

export const JumpCutsTimingBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 3px;
  color: #8fa9af;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #253a49;
    color: #4dbd94;
    border-color: #4dbd94;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const JumpCutsTimingDisplay = styled(Text)`
  font-size: 11px;
  font-weight: 600;
  color: #f7fcfd;
  font-family: monospace;
  min-width: 28px;
  text-align: center;
`;

export const ClipNumber = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: #8fa9af;
  margin-right: 8px;
`;

export const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #557077;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #f95370;
    background: rgba(249, 83, 112, 0.15);
  }
`;

export const PanelFooter = styled(Box)`
  padding: 16px;
  border-top: 1px solid #1e2e3a;
  background: #0f171d;
`;

export const ClipTitleSection = styled(Box)`
  margin-bottom: 12px;
`;

export const ClipTitleLabel = styled(Text)`
  font-size: 13px;
  color: #8fa9af;
  margin-bottom: 8px;
  display: block;
`;

export const ClipTitleInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: #0b1116;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #f7fcfd;
  font-size: 13px;
  box-sizing: border-box;
  transition: all 0.15s ease;

  &::placeholder {
    color: #557077;
  }

  &:focus {
    outline: none;
    border-color: #71aaff;
    background: #131d25;
  }

  &:hover:not(:focus) {
    border-color: #3a5066;
  }
`;

export const TotalDuration = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const CreateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 14px;
  background: linear-gradient(135deg, #4dbd94 0%, #3a9d7a 100%);
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5cd4a6 0%, #4dbd94 100%);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Tooltip = styled(Box)`
  position: fixed;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 8px;
  padding: 12px;
  z-index: 100;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  max-width: 220px;
`;

export const TooltipTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #253a49;
`;

export const TooltipRow = styled(Box)`
  font-size: 12px;
  color: #8fa9af;
  margin-bottom: 4px;

  span {
    color: #557077;
    margin-right: 4px;
  }
`;

export const TooltipAction = styled(Text)`
  font-size: 11px;
  color: #4dbd94;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #253a49;
`;

export const TooltipMerge = styled(Text)`
  font-size: 11px;
  color: #71aaff;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #253a49;
`;

export const LoadingContainer = styled(Flex)`
  height: calc(100vh - 73px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #0b1116;
  gap: 24px;
`;

export const LoadingSpinner = styled(Box)`
  width: 48px;
  height: 48px;
  border: 3px solid rgba(77, 189, 148, 0.15);
  border-top-color: #4dbd94;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingContent = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const LoadingTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: #f7fcfd;
`;

export const LoadingSubtitle = styled(Text)`
  font-size: 13px;
  color: #557077;
`;

export const LoadingProgress = styled(Box)`
  width: 200px;
  height: 4px;
  background: rgba(143, 169, 175, 0.15);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
`;

export const LoadingProgressBar = styled(Box)`
  height: 100%;
  width: 30%;
  background: linear-gradient(90deg, #4dbd94, #71aaff);
  border-radius: 2px;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      width: 0%;
      margin-left: 0%;
    }
    50% {
      width: 40%;
      margin-left: 30%;
    }
    100% {
      width: 0%;
      margin-left: 100%;
    }
  }
`;

export const WelcomeModalOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

export const WelcomeModalContent = styled(Box)`
  background: linear-gradient(135deg, #131d25 0%, #1a2933 100%);
  border: 1px solid #253a49;
  border-radius: 16px;
  padding: 32px;
  max-width: 520px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, #4dbd94, #71aaff);
  }
`;

export const WelcomeModalTitle = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 8px;
`;

export const WelcomeModalSubtitle = styled(Text)`
  font-size: 14px;
  color: #71aaff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const WelcomeModalSection = styled(Box)`
  margin-bottom: 20px;
`;

export const WelcomeModalSectionTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #8fa9af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

export const WelcomeModalList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const WelcomeModalListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #8fa9af;
  line-height: 1.5;
`;

export const WelcomeModalListNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  background: rgba(77, 189, 148, 0.15);
  border: 1px solid rgba(77, 189, 148, 0.3);
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  color: #4dbd94;
  flex-shrink: 0;
`;

export const WelcomeModalShortcuts = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(143, 169, 175, 0.1);
`;

export const WelcomeModalShortcutItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8fa9af;
`;

export const WelcomeModalWarning = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(232, 169, 62, 0.1);
  border: 1px solid rgba(232, 169, 62, 0.25);
  border-radius: 8px;
  margin-bottom: 20px;

  svg {
    color: #e8a93e;
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    font-size: 12px;
    color: #8fa9af;
    line-height: 1.5;
  }
`;

export const WelcomeModalDiscord = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(88, 101, 242, 0.15);
  border: 1px solid rgba(88, 101, 242, 0.3);
  border-radius: 8px;
  color: #8fa9af;
  font-size: 13px;
  text-decoration: none;
  transition: all 0.15s ease;
  margin-bottom: 20px;

  svg {
    color: #5865f2;
  }

  &:hover {
    background: rgba(88, 101, 242, 0.25);
    border-color: rgba(88, 101, 242, 0.5);
    color: #f7fcfd;
  }
`;

export const WelcomeModalButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  background: linear-gradient(135deg, #4dbd94, #3a9d7a);
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: linear-gradient(135deg, #5cd4a6, #4dbd94);
    transform: translateY(-1px);
  }
`;

export const WelcomeModalKeyboard = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  background: rgba(143, 169, 175, 0.15);
  border: 1px solid rgba(143, 169, 175, 0.3);
  border-radius: 4px;
  font-size: 11px;
  font-family: monospace;
  color: #8fa9af;
`;

export const UpsellBadgeContainer = styled.div`
  margin-bottom: 16px;
`;

export const SeeAllPlansLink = styled(Text)`
  color: rgba(143, 169, 175, 0.8);
  cursor: pointer;
  font-size: 12px;
  margin-top: 12px;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover {
    color: #f7fcfd;
  }
`;

// ==========================================
// Suggested Moments Panel
// ==========================================

export const SuggestionsPanel = styled(Box)<{ collapsed?: boolean }>`
  background: #131d25;
  border-bottom: 1px solid #1e2e3a;
  overflow: hidden;
  transition: all 0.2s ease;
  max-height: ${({ collapsed }) => (collapsed ? "48px" : "280px")};
`;

export const SuggestionsPanelHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(143, 169, 175, 0.05);
  }
`;

export const SuggestionsPanelTitle = styled(Flex)`
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #f7fcfd;

  .icon {
    font-size: 16px;
  }

  .count {
    background: rgba(77, 189, 148, 0.2);
    color: #4dbd94;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
  }
`;

export const SuggestionsPanelContent = styled(Box)`
  padding: 0 16px 16px;
`;

export const SuggestionsGrid = styled(Flex)`
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

export const SuggestionCard = styled(Flex)<{
  $momentType: string;
  $isAdded?: boolean;
}>`
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: ${({ $isAdded }) =>
    $isAdded ? "rgba(77, 189, 148, 0.15)" : "#16232c"};
  border: 1px solid
    ${({ $momentType, $isAdded }) => {
      if ($isAdded) return "#4dbd94";
      switch ($momentType) {
        case "ace":
          return "rgba(255, 215, 0, 0.4)";
        case "quad-kill":
          return "rgba(255, 107, 107, 0.4)";
        case "triple-kill":
          return "rgba(113, 170, 255, 0.4)";
        default:
          return "#253a49";
      }
    }};
  border-radius: 8px;
  cursor: ${({ $isAdded }) => ($isAdded ? "default" : "pointer")};
  transition: all 0.15s ease;
  opacity: ${({ $isAdded }) => ($isAdded ? 0.7 : 1)};
  flex-shrink: 0;

  &:hover {
    ${({ $isAdded }) =>
      !$isAdded &&
      `
      background: #1a2a36;
      transform: translateY(-1px);
    `}
  }
`;

export const SuggestionIcon = styled.span<{ $momentType: string }>`
  font-size: 18px;
  line-height: 1;
`;

export const SuggestionAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #253a49;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8fa9af;
  font-size: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const SuggestionInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SuggestionLabel = styled(Text)<{ $momentType: string }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $momentType }) => {
    switch ($momentType) {
      case "ace":
        return "#ffd700";
      case "quad-kill":
        return "#ff6b6b";
      case "triple-kill":
        return "#71aaff";
      default:
        return "#4dbd94";
    }
  }};
`;

export const SuggestionMeta = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #8fa9af;

  .player {
    color: #f7fcfd;
    font-weight: 500;
  }

  .separator {
    color: #557077;
  }
`;

export const SuggestionAddIcon = styled(Flex)<{ $isAdded?: boolean }>`
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${({ $isAdded }) =>
    $isAdded ? "rgba(77, 189, 148, 0.3)" : "rgba(77, 189, 148, 0.15)"};
  border-radius: 50%;
  color: #4dbd94;
  margin-left: auto;
  flex-shrink: 0;
  transition: all 0.15s ease;

  ${SuggestionCard}:hover & {
    ${({ $isAdded }) =>
      !$isAdded &&
      `
      background: rgba(77, 189, 148, 0.3);
      transform: scale(1.1);
    `}
  }
`;

export const SuggestionsActions = styled(Flex)`
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(30, 46, 58, 0.5);
`;

export const SuggestionActionButton = styled.button<{
  $variant?: "primary" | "secondary";
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ $variant }) =>
    $variant === "primary"
      ? "linear-gradient(135deg, #4dbd94 0%, #3a9d7a 100%)"
      : "rgba(143, 169, 175, 0.1)"};
  border: 1px solid
    ${({ $variant }) => ($variant === "primary" ? "transparent" : "#253a49")};
  border-radius: 6px;
  color: ${({ $variant }) => ($variant === "primary" ? "#fff" : "#8fa9af")};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === "primary"
        ? "linear-gradient(135deg, #5cd4a6 0%, #4dbd94 100%)"
        : "rgba(143, 169, 175, 0.2)"};
    ${({ $variant }) => $variant !== "primary" && "color: #f7fcfd;"}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SuggestionsEmpty = styled(Flex)`
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #557077;
  font-size: 13px;
  text-align: center;
`;

export const MomentBadge = styled(Flex)<{ $momentType: string }>`
  position: absolute;
  top: -20px;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: ${({ $momentType }) => {
    switch ($momentType) {
      case "ace":
        return "rgba(255, 215, 0, 0.2)";
      case "quad-kill":
        return "rgba(255, 107, 107, 0.2)";
      case "triple-kill":
        return "rgba(113, 170, 255, 0.2)";
      default:
        return "rgba(77, 189, 148, 0.2)";
    }
  }};
  border: 1px solid
    ${({ $momentType }) => {
      switch ($momentType) {
        case "ace":
          return "rgba(255, 215, 0, 0.5)";
        case "quad-kill":
          return "rgba(255, 107, 107, 0.5)";
        case "triple-kill":
          return "rgba(113, 170, 255, 0.5)";
        default:
          return "rgba(77, 189, 148, 0.5)";
      }
    }};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  color: ${({ $momentType }) => {
    switch ($momentType) {
      case "ace":
        return "#ffd700";
      case "quad-kill":
        return "#ff6b6b";
      case "triple-kill":
        return "#71aaff";
      default:
        return "#4dbd94";
    }
  }};
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

// ==========================================
// Wizard Onboarding Flow
// ==========================================

export const WizardOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(11, 17, 22, 0.85);
  backdrop-filter: blur(8px);
  z-index: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px;
  overflow-y: auto;
`;

export const WizardContainer = styled(Box)`
  max-width: 600px;
  width: 100%;
  text-align: center;
  padding-bottom: 40px;
`;

export const WizardStep = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: #4dbd94;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

export const WizardTitle = styled(Text)`
  font-size: 28px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 8px;
`;

export const WizardSubtitle = styled(Text)`
  font-size: 14px;
  color: #8fa9af;
  margin-bottom: 32px;
  line-height: 1.5;
`;

export const WizardTeamsContainer = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  max-width: 700px;
  margin-bottom: 24px;
`;

export const WizardTeamColumn = styled.div<{ $team: "ct" | "t" }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: ${({ $team }) =>
    $team === "ct" ? "rgba(92, 155, 255, 0.05)" : "rgba(232, 169, 62, 0.05)"};
  border: 1px solid
    ${({ $team }) =>
      $team === "ct" ? "rgba(92, 155, 255, 0.2)" : "rgba(232, 169, 62, 0.2)"};
  border-radius: 8px;
`;

export const WizardTeamHeader = styled.div<{ $team: "ct" | "t" }>`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $team }) => ($team === "ct" ? "#5C9BFF" : "#E8A93E")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid
    ${({ $team }) =>
      $team === "ct" ? "rgba(92, 155, 255, 0.2)" : "rgba(232, 169, 62, 0.2)"};
`;

export const WizardPlayersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const currentUserPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(77, 189, 148, 0.2);
    border-color: rgba(77, 189, 148, 0.5);
  }
  50% {
    box-shadow: 0 0 20px 4px rgba(77, 189, 148, 0.5);
    border-color: rgba(77, 189, 148, 0.8);
  }
`;

export const WizardPlayerCard = styled.button<{
  $selected?: boolean;
  $team?: "ct" | "t" | null;
  $isCurrentUser?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  position: relative;
  background: ${({ $selected, $isCurrentUser }) =>
    $isCurrentUser
      ? $selected
        ? "rgba(77, 189, 148, 0.25)"
        : "rgba(77, 189, 148, 0.1)"
      : $selected
      ? "rgba(113, 170, 255, 0.2)"
      : "#16232c"};
  border: 2px solid
    ${({ $selected, $isCurrentUser, $team }) =>
      $isCurrentUser
        ? $selected
          ? "#4dbd94"
          : "rgba(77, 189, 148, 0.5)"
        : $selected
        ? "#71aaff"
        : $team === "ct"
        ? "rgba(92, 155, 255, 0.3)"
        : $team === "t"
        ? "rgba(232, 169, 62, 0.3)"
        : "#253a49"};
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  animation: ${({ $isCurrentUser, $selected }) =>
    $isCurrentUser && !$selected
      ? css`
          ${currentUserPulse} 2.5s ease-in-out infinite
        `
      : "none"};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-color: ${({ $isCurrentUser, $team }) =>
      $isCurrentUser
        ? "#4dbd94"
        : $team === "ct"
        ? "#5c9bff"
        : $team === "t"
        ? "#e8a93e"
        : "#71aaff"};
    animation: none;
  }
`;

export const WizardPlayerAction = styled.div<{ $isAdded?: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $isAdded }) =>
    $isAdded ? "rgba(77, 189, 148, 0.3)" : "rgba(113, 170, 255, 0.2)"};
  color: ${({ $isAdded }) => ($isAdded ? "#4dbd94" : "#71aaff")};
  transition: all 0.15s ease;

  ${WizardPlayerCard}:hover & {
    background: ${({ $isAdded }) =>
      $isAdded ? "rgba(77, 189, 148, 0.4)" : "rgba(113, 170, 255, 0.3)"};
    transform: translateY(-50%) scale(1.1);
  }
`;

export const WizardPlayerAvatar = styled(Flex)<{
  $team?: "ct" | "t" | null;
  $isCurrentUser?: boolean;
}>`
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ $isCurrentUser, $team }) =>
    $isCurrentUser
      ? "#4dbd94"
      : $team === "ct"
      ? "rgba(92, 155, 255, 0.2)"
      : $team === "t"
      ? "rgba(232, 169, 62, 0.2)"
      : "#253a49"};
  color: ${({ $isCurrentUser, $team }) =>
    $isCurrentUser
      ? "#0b1116"
      : $team === "ct"
      ? "#5c9bff"
      : $team === "t"
      ? "#e8a93e"
      : "#8fa9af"};
  flex-shrink: 0;
  font-weight: 600;
`;

export const WizardPlayerAvatarImg = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
`;

export const WizardPlayerInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const WizardPlayerName = styled(Text)<{ $isCurrentUser?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $isCurrentUser }) => ($isCurrentUser ? "#5cd4a6" : "#f7fcfd")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const WizardPlayerStats = styled(Flex)`
  gap: 12px;
  margin-top: 4px;
  font-size: 12px;
  color: #8fa9af;

  .kills {
    color: #8fa9af;
  }

  .deaths {
    color: #8fa9af;
  }
`;

export const WizardTeamDivider = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  font-size: 12px;
  font-weight: 600;
  color: #557077;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(85, 112, 119, 0.3),
      transparent
    );
  }
`;

export const WizardContinueButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #4dbd94 0%, #3a9d7a 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(77, 189, 148, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const WizardSkipButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #3a5066;
  border-radius: 8px;
  color: #8fa9af;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-top: 16px;

  &:hover {
    border-color: #557077;
    color: #f7fcfd;
  }
`;

export const WizardMomentsSection = styled(Box)`
  margin-top: 24px;
  text-align: left;
`;

export const WizardMomentsTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 16px;
`;

export const WizardMomentsList = styled(Flex)`
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(143, 169, 175, 0.1);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(143, 169, 175, 0.3);
    border-radius: 3px;
  }
`;

export const WizardMomentCard = styled(Flex)<{
  $momentType: string;
  $isAdded?: boolean;
}>`
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ $isAdded }) =>
    $isAdded ? "rgba(77, 189, 148, 0.15)" : "#16232c"};
  border: 1px solid
    ${({ $momentType, $isAdded }) => {
      if ($isAdded) return "#4dbd94";
      switch ($momentType) {
        case "ace":
          return "rgba(255, 215, 0, 0.4)";
        case "quad-kill":
          return "rgba(255, 107, 107, 0.4)";
        case "triple-kill":
          return "rgba(113, 170, 255, 0.4)";
        default:
          return "#253a49";
      }
    }};
  border-radius: 10px;
  cursor: ${({ $isAdded }) => ($isAdded ? "default" : "pointer")};
  transition: all 0.15s ease;
  opacity: ${({ $isAdded }) => ($isAdded ? 0.7 : 1)};

  &:hover {
    ${({ $isAdded }) =>
      !$isAdded &&
      `
      background: #1a2a36;
      transform: translateX(4px);
    `}
  }
`;

// ==========================================
// Player Picker Modal
// ==========================================

export const PlayerPickerOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

export const PlayerPickerModal = styled(Box)`
  background: linear-gradient(135deg, #131d25 0%, #1a2933 100%);
  border: 1px solid #253a49;
  border-radius: 16px;
  padding: 24px;
  max-width: 480px;
  width: 90%;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
`;

export const PlayerPickerHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const PlayerPickerTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: #f7fcfd;
`;

export const PlayerPickerCloseBtn = styled.button`
  background: none;
  border: none;
  color: #8fa9af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #f7fcfd;
  }
`;

export const PlayerPickerList = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(143, 169, 175, 0.1);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(143, 169, 175, 0.3);
    border-radius: 3px;
  }
`;

export const PlayerPickerItem = styled.button<{
  $team?: "ct" | "t" | null;
  $isCurrentUser?: boolean;
  $isSelected?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: ${({ $isSelected, $isCurrentUser }) =>
    $isSelected
      ? "rgba(77, 189, 148, 0.15)"
      : $isCurrentUser
      ? "rgba(77, 189, 148, 0.1)"
      : "#16232c"};
  border: 2px solid
    ${({ $team, $isSelected, $isCurrentUser }) =>
      $isSelected
        ? "#4dbd94"
        : $isCurrentUser
        ? "rgba(77, 189, 148, 0.5)"
        : $team === "ct"
        ? "rgba(92, 155, 255, 0.3)"
        : $team === "t"
        ? "rgba(232, 169, 62, 0.3)"
        : "#253a49"};
  border-radius: 10px;
  cursor: ${({ $isSelected }) => ($isSelected ? "default" : "pointer")};
  transition: all 0.15s ease;
  opacity: ${({ $isSelected }) => ($isSelected ? 0.6 : 1)};
  width: 100%;
  text-align: left;

  &:hover {
    ${({ $isSelected, $isCurrentUser }) =>
      !$isSelected &&
      `
      background: ${$isCurrentUser ? "rgba(77, 189, 148, 0.2)" : "#1a2a36"};
      transform: translateX(4px);
      border-color: ${$isCurrentUser ? "#4dbd94" : "#557077"};
    `}
  }
`;

export const PlayerPickerAvatar = styled.div<{
  $team?: "ct" | "t" | null;
  $isCurrentUser?: boolean;
}>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid
    ${({ $team, $isCurrentUser }) =>
      $isCurrentUser
        ? "#4dbd94"
        : $team === "ct"
        ? "rgba(92, 155, 255, 0.5)"
        : $team === "t"
        ? "rgba(232, 169, 62, 0.5)"
        : "#253a49"};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isCurrentUser }) =>
    $isCurrentUser ? "rgba(77, 189, 148, 0.2)" : "#1a2933"};
  color: ${({ $isCurrentUser }) => ($isCurrentUser ? "#4dbd94" : "#8fa9af")};
`;

export const PlayerPickerAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const PlayerPickerInfo = styled(Flex)`
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

export const PlayerPickerName = styled(Text)<{ $isCurrentUser?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $isCurrentUser }) => ($isCurrentUser ? "#4dbd94" : "#f7fcfd")};
`;

export const PlayerPickerTeam = styled(Text)<{ $team?: "ct" | "t" | null }>`
  font-size: 11px;
  color: ${({ $team }) =>
    $team === "ct" ? "#5c9bff" : $team === "t" ? "#e8a93e" : "#8fa9af"};
`;

export const PlayerPickerCheck = styled.div`
  color: #4dbd94;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ==========================================
// Welcome Screen Components
// ==========================================

export const WelcomeScreen = styled(Flex)`
  flex: 1;
  align-items: center;
  justify-content: center;
  background: #0d1519;
`;

export const WelcomeCard = styled(Flex)`
  flex-direction: column;
  align-items: center;
  padding: 56px 72px;
  background: linear-gradient(
    135deg,
    rgba(19, 29, 37, 0.95) 0%,
    rgba(26, 41, 51, 0.95) 100%
  );
  border: 1px solid rgba(77, 189, 148, 0.2);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${borderGlow} 4s ease-in-out infinite;
  min-width: 480px;
`;

export const WelcomeIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: rgba(77, 189, 148, 0.15);
  border: 2px solid rgba(77, 189, 148, 0.3);
  border-radius: 50%;
  margin-bottom: 20px;
  color: #4dbd94;
`;

export const WelcomeTitle = styled(Text)`
  font-size: 26px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 12px;
`;

export const WelcomeSubtitle = styled(Text)`
  font-size: 14px;
  color: #8fa9af;
  margin-bottom: 32px;
  text-align: center;
  max-width: 340px;
  line-height: 1.6;
`;

export const WelcomeSelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #4dbd94 0%, #3a9d7a 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(77, 189, 148, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;
